import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { SsoCredentials } from '../db/auth/types';
import { setTitleAndSubtitle } from '../db/ppt';
import { getSession as getUserSession, setSession as setUserSession } from '../db/session';
import { UserSession } from '../db/types';

interface AppContextProps {
  isNewPresentation: boolean | null;
  setIsNewPresentation: Dispatch<SetStateAction<boolean | null>>;
  presentationId: string | undefined;
  setPresentationId: Dispatch<SetStateAction<string | undefined>>;
  activeSlideId: string | undefined;
  setActiveSlideId: Dispatch<SetStateAction<string | undefined>>;
  isPresenting: boolean;
  setIsPresenting: Dispatch<SetStateAction<boolean>>;
  slideTitleAndSubtitle: { title: string; subtitle: string };
  setSlideTitleAndSubtitle: ({ title, subtitle }: { title: string; subtitle: string }) => void;
  session: UserSession | null;
  setSession: (session: UserSession | null) => void;
  currentCourseId: MongoId | null;
  setCurrentCourseId: Dispatch<SetStateAction<MongoId | null>>;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  ssoCredentials: SsoCredentials | null;
  setSsoCredentials: Dispatch<SetStateAction<SsoCredentials | null>>;
  avatarsById: Record<string, string>;
  setAvatarsById: Dispatch<SetStateAction<Record<string, string>>>;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('Using useAppContext outside AppContextProvider is not allowed.');
  }
  return context;
}

interface UseAppContextProvider {
  presentationId: string;
  isNewPresentation: boolean;
  activeSlideId: string;
}

function useAppContextProviderVM({
  presentationId: initialPresentationId,
  isNewPresentation: initialIsNewPresentation,
  activeSlideId: initialActiveSlideId,
}: UseAppContextProvider) {
  const [isNewPresentation, setIsNewPresentation] = useState<boolean | null>(initialIsNewPresentation);
  const [presentationId, setPresentationId] = useState<string | undefined>(initialPresentationId);
  const [activeSlideId, setActiveSlideId] = useState<string | undefined>(initialActiveSlideId);
  const [isPresenting, setIsPresenting] = useState(false);
  const [slideTitleAndSubtitle, _setSlideTitleAndSubtitle] = useState({ title: '', subtitle: '' });

  const setSlideTitleAndSubtitle = useCallback(
    ({ title, subtitle }: { title: string; subtitle: string }) => {
      if (!activeSlideId) return;
      setTitleAndSubtitle(activeSlideId, title, subtitle);
      _setSlideTitleAndSubtitle({ title, subtitle });
    },
    [activeSlideId]
  );

  const [session, _setSession] = useState<UserSession | null>(getUserSession());

  const [ssoCredentials, setSsoCredentials] = useState<SsoCredentials | null>(null);

  const setSession = useCallback((session: UserSession | null) => {
    if (!session) {
      setUserSession(null);
      _setSession(null);
      return;
    }

    setUserSession(session);
    _setSession(session);
  }, []);

  const [currentCourseId, setCurrentCourseId] = useState<MongoId | null>(null);
  const [error, setError] = useState('');
  const [avatarsById, setAvatarsById] = useState({});

  return useMemo(
    () => ({
      isNewPresentation,
      setIsNewPresentation,
      presentationId,
      setPresentationId,
      activeSlideId,
      setActiveSlideId,
      isPresenting,
      setIsPresenting,
      slideTitleAndSubtitle,
      setSlideTitleAndSubtitle,
      session,
      setSession,
      currentCourseId,
      setCurrentCourseId,
      error,
      setError,
      ssoCredentials,
      setSsoCredentials,
      avatarsById,
      setAvatarsById,
    }),
    [
      isNewPresentation,
      presentationId,
      activeSlideId,
      isPresenting,
      slideTitleAndSubtitle,
      setSlideTitleAndSubtitle,
      session,
      setSession,
      currentCourseId,
      error,
      ssoCredentials,
      avatarsById,
    ]
  );
}

export interface AppContextProviderProps extends UseAppContextProvider {
  children: ReactNode;
}

export function AppContextProvider({ children, ...vmProps }: AppContextProviderProps) {
  const providerValue = useAppContextProviderVM(vmProps);
  return <AppContext.Provider value={providerValue}>{children}</AppContext.Provider>;
}
